import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiAlertTriangle, FiMoreVertical } from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layout/DashboardLayout";

const baseURL = process.env.REACT_APP_API_BASE_URL || "https://digitalapi.palsonsderma.com";

// Utility to format ISO date to human readable (null / invalid → em dash)
function formatDate(isoString) {
  if (isoString == null || isoString === "") return "—";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "—";

  const options = {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };

  return date.toLocaleString("en-US", options);
}

function platformRow(info) {
  return info != null && typeof info === "object" ? info : {};
}

function PlatformAdminActions({
  platformKey,
  row,
  navigate,
  onSyncCheckpoint,
  syncingPlatform,
}) {
  const p = platformKey.toLowerCase();
  const runByDateLabel =
    p === "shopify"
      ? "Manual poll (Shopify)"
      : p === "amazon"
        ? "Manual poll (Amazon)"
        : p === "flipkart"
          ? "Manual poll (Flipkart)"
          : "Manual poll";
  const manualPollCheckpoint = row.latestManualPoll?.checkpointDate;
  const showSyncMenu = p === "amazon";
  const isSyncing = syncingPlatform === p;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  if (!["amazon", "shopify", "flipkart"].includes(p)) {
    return <span className="text-gray-400">—</span>;
  }

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() =>
          navigate(
            `/analytics/manual-polling?platform=${encodeURIComponent(p)}`,
            { state: { platform: p } },
          )
        }
        className={
          p === "shopify"
            ? "px-3 py-1.5 text-sm bg-emerald-700 text-white rounded-md hover:bg-emerald-800"
            : p === "flipkart"
              ? "px-3 py-1.5 text-sm bg-amber-600 text-white rounded-md hover:bg-amber-700"
              : "px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
        }
      >
        {runByDateLabel}
      </button>
      {showSyncMenu && (
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label="More actions"
            aria-expanded={menuOpen}
            className="p-2 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100"
          >
            <FiMoreVertical size={16} />
          </button>
          {menuOpen && (
            <div className="absolute right-0 mt-1 min-w-[12rem] rounded-md border border-gray-200 bg-white shadow-lg z-20 py-1">
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  onSyncCheckpoint(p, manualPollCheckpoint);
                }}
                disabled={isSyncing}
                className="block w-full px-4 py-2 text-left text-sm text-gray-800 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSyncing ? "Syncing..." : "Sync poll dates"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const PollingStatus = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = Boolean(user?.isPrimary);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notificationEmail, setNotificationEmail] = useState("");
  const [notificationEmailSaving, setNotificationEmailSaving] = useState(false);
  const [notificationEmailMessage, setNotificationEmailMessage] = useState(null);
  const [showNotificationEmailDialog, setShowNotificationEmailDialog] = useState(false);
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [syncingPlatform, setSyncingPlatform] = useState(null);
  const [syncMessage, setSyncMessage] = useState(null);
  const [syncPollModal, setSyncPollModal] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${baseURL}/data/webhook/polling-status`, {
        method: "GET",
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch polling status");
      const result = await response.json();
      setData(result.data);
      setNotificationEmail(result.data?.notificationEmail ?? "");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveNotificationEmail = async () => {
    setNotificationEmailMessage(null);
    setNotificationEmailSaving(true);
    try {
      const response = await fetch(`${baseURL}/data/webhook/polling-notification-email`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: notificationEmail.trim() }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result?.message || "Failed to save");
      setNotificationEmailMessage("Notification email saved.");
      if (result.data?.notificationEmail !== undefined) {
        setNotificationEmail(result.data.notificationEmail ?? "");
      }
    } catch (err) {
      setNotificationEmailMessage(err.message || "Failed to save.");
    } finally {
      setNotificationEmailSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    setNotificationEmailMessage(null);
    setTestEmailSending(true);
    try {
      const response = await fetch(`${baseURL}/data/webhook/polling-notification-email/test`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: notificationEmail.trim() || undefined }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result?.message || "Failed to send test email");
      setNotificationEmailMessage("Test email sent. Check your inbox.");
    } catch (err) {
      setNotificationEmailMessage(err.message || "Failed to send test email.");
    } finally {
      setTestEmailSending(false);
    }
  };

  const handleOpenSyncPollModal = (platform, checkpointDate) => {
    setSyncPollModal({ platform, checkpointDate: checkpointDate ?? null });
  };

  const executeSyncPollDates = async () => {
    if (!syncPollModal?.platform) return;

    const { platform } = syncPollModal;
    setSyncPollModal(null);
    setSyncMessage(null);
    setSyncingPlatform(platform);

    try {
      const response = await fetch(`${baseURL}/data/polling/sync-last-new-order-polled`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ platform }),
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result?.message || "Failed to sync poll dates");
      }
      setSyncMessage(result.message || "Poll dates synced.");
      await fetchData();
    } catch (err) {
      setSyncMessage(err.message || "Failed to sync poll dates.");
    } finally {
      setSyncingPlatform(null);
    }
  };

  const syncPollTargetLabel = syncPollModal?.checkpointDate
    ? formatDate(syncPollModal.checkpointDate)
    : "your latest successful manual poll";

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold">Polling Status</h1>
        {(data !== null || !loading) && (
          <div className="flex flex-wrap gap-2">
            {isAdmin && (
              <button
                type="button"
                onClick={() => navigate("/analytics/cron-logs")}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                View cron logs
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowNotificationEmailDialog(true)}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              Configure cron failure notification email
            </button>
          </div>
        )}
      </div>
      {loading && <div>Loading...</div>}
      {error && <div className="text-red-500">{error}</div>}
      {syncMessage && (
        <div
          className={`mb-4 text-sm ${
            syncMessage.includes("updated") ||
            syncMessage.includes("synced") ||
            syncMessage.includes("already")
              ? "text-green-600 dark:text-green-400"
              : "text-red-600 dark:text-red-400"
          }`}
        >
          {syncMessage}
        </div>
      )}

      {/* Cron failure notification email – fixed dialog */}
      {showNotificationEmailDialog && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowNotificationEmailDialog(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="notification-email-dialog-title"
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 id="notification-email-dialog-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Cron failure notification email
              </h2>
              <button
                type="button"
                onClick={() => setShowNotificationEmailDialog(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              When a polling cron job fails, an alert will be sent to this email (if set and SMTP is configured on the server).
            </p>
            <div className="flex flex-col gap-3">
              <input
                type="email"
                value={notificationEmail}
                onChange={(e) => setNotificationEmail(e.target.value)}
                placeholder="e.g. alerts@example.com"
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 w-full"
              />
              {notificationEmailMessage && (
                <p className={`text-sm ${notificationEmailMessage.startsWith("Notification") || notificationEmailMessage.startsWith("Test email") ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                  {notificationEmailMessage}
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={handleSendTestEmail}
                disabled={testEmailSending || notificationEmailSaving}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {testEmailSending ? "Sending..." : "Send test email"}
              </button>
              <button
                type="button"
                onClick={() => setShowNotificationEmailDialog(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveNotificationEmail}
                disabled={notificationEmailSaving || testEmailSending}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {notificationEmailSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {syncPollModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setSyncPollModal(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="sync-poll-dates-dialog-title"
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2
                id="sync-poll-dates-dialog-title"
                className="text-lg font-semibold text-gray-900 dark:text-gray-100"
              >
                Sync poll dates
              </h2>
              <button
                type="button"
                onClick={() => setSyncPollModal(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Set <span className="font-medium">Last New Order Polled</span> and{" "}
              <span className="font-medium">Last Order Status Update</span> to{" "}
              <span className="font-medium">{syncPollTargetLabel}</span>.
            </p>

            <div className="flex gap-3 rounded-md border border-red-200 bg-red-50 p-4 mb-6">
              <FiAlertTriangle
                className="text-red-600 flex-shrink-0 mt-0.5"
                size={20}
                aria-hidden
              />
              <div className="text-sm text-red-800">
                <p className="font-semibold mb-1">Warning</p>
                <p>
                  Only do this if manual polling already covered every order through that date.
                  After sync, Amazon cron will skip older orders and will not backfill anything you missed.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setSyncPollModal(null)}
                disabled={syncingPlatform === syncPollModal.platform}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={executeSyncPollDates}
                disabled={syncingPlatform === syncPollModal.platform}
                className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {syncingPlatform === syncPollModal.platform
                  ? "Syncing..."
                  : "Sync poll dates"}
              </button>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* Table for large screens */}
          <div className="overflow-x-auto hidden lg:block">
            <table className="min-w-full bg-white rounded shadow text-sm">
              <thead>
                <tr>
                  <th className="px-2 py-2 text-left">Platform</th>
                  <th className="px-2 py-2 text-left">Order Update Index</th>
                  <th className="px-2 py-2 text-left whitespace-nowrap">
                    Last Order Status Update
                  </th>
                  <th className="px-2 py-2 text-left whitespace-nowrap">
                    Last New Order Polled
                  </th>
                  {isAdmin && <th className="px-2 py-2 text-left">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {Object.entries(data)
                  .filter(([key]) => key !== "notificationEmail")
                  .map(([platform, info]) => {
                    const row = platformRow(info);
                    return (
                  <tr key={platform} className="border-t">
                    <td className="px-2 py-2 capitalize font-medium">
                      {platform}
                    </td>
                    <td className="px-2 py-2 text-gray-600">
                      {row.orderUpdateIndex != null && row.orderUpdateIndex !== ""
                        ? row.orderUpdateIndex
                        : "—"}
                    </td>
                    <td className="px-2 py-2">
                      {formatDate(row.lastOrderStatusUpdatePolledAt)}
                    </td>
                    <td className="px-2 py-2">
                      {formatDate(row.lastNewOrderPolledAt)}
                    </td>
                    {isAdmin && (
                      <td className="px-2 py-2">
                        <PlatformAdminActions
                          platformKey={platform}
                          row={row}
                          navigate={navigate}
                          onSyncCheckpoint={handleOpenSyncPollModal}
                          syncingPlatform={syncingPlatform}
                        />
                      </td>
                    )}
                  </tr>
                );
                })}
              </tbody>
            </table>
          </div>

          {/* Cards for small screens */}
          <div className="space-y-4 lg:hidden">
            {Object.entries(data)
              .filter(([key]) => key !== "notificationEmail")
              .map(([platform, info]) => {
                const row = platformRow(info);
                return (
              <div
                key={platform}
                className="bg-white rounded shadow p-4 flex flex-col"
              >
                <div className="flex items-center mb-2">
                  <span className="text-lg font-semibold capitalize mr-2">
                    {platform}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <div>
                    <span className="font-medium">Order Update Index: </span>
                    <span className="text-gray-600">
                      {row.orderUpdateIndex != null && row.orderUpdateIndex !== ""
                        ? row.orderUpdateIndex
                        : "—"}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium">
                      Last Order Status Update:{" "}
                    </span>
                    <span>
                      {formatDate(row.lastOrderStatusUpdatePolledAt)}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium">Last New Order Polled: </span>
                    <span>{formatDate(row.lastNewOrderPolledAt)}</span>
                  </div>
                </div>
                {isAdmin && (
                  <div className="mt-3">
                    <PlatformAdminActions
                      platformKey={platform}
                      row={row}
                      navigate={navigate}
                      onSyncCheckpoint={handleOpenSyncPollModal}
                      syncingPlatform={syncingPlatform}
                    />
                  </div>
                )}
              </div>
            );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default PollingStatus;
